import React, { useState, useEffect } from "react";
import Color from 'color';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import withCurrentUser from '../../../../hocs/with_current_user.js';
import { compose } from 'redux';
import { Link } from 'react-router-dom';
import CloseIcon from '@material-ui/icons/Close';
import { IconButton, CircularProgress } from '@material-ui/core';

import { UserModel } from "../../../../models/users/user_model.js";

import HtmlContent from "../../../../base/components/html_content.js";
import I18n from "../../../../base/i18n.js";

import WebIdIdentifiedInfoClosedStorage from "../../../../utils/web_id_identified_info_closed_storage.js";

import Routes from "../../../routes.js";

const blockStyles = makeStyles(theme => ({
  root: {
    color: theme.palette.gray2,
    padding: "20px 25px",
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: theme.typography.fontWeightLight,
    "&$identificationRequired": {
      backgroundColor: theme.palette.pink
    },
    "&$identificationPending": {
      backgroundColor: theme.palette.grayLight
    },
    "&$identified": {
      backgroundColor: Color(theme.palette.blueMain).fade(0.8).string()
    },
    ['@media (max-width: 768px)']: {
      padding: "16px 16px",
    },
  },
  closeButton: {
    position: "absolute",
    width: "28px",
    height: "28px",
    top: "15px",
    right: "18px",
    ['@media (max-width: 768px)']: {
      position: "relative",
      flexBasis: "28px",
      top: "inherit",
      right: "inherit",
      marginRight: "-8px"
    },
  },
  closeIcon: {
    width: "20px",
    height: "20px",
  },
  content: {
    maxWidth: "66%",
    margin: "0 auto",
    textAlign: "center",
    ['@media (max-width: 768px)']: {
      maxWidth: "100%",
    },
  },
  title: {
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: "14px",
    lineHeight: "18px",
    textAlign: "center",
    ['@media (max-width: 768px)']: {
      lineHeight: "16px",
      textAlign: "left",
    },
  },
  text: {
    marginTop: "12px",
    fontSize: "12px",
    lineHeight: "16px",
    textAlign: "center",
    ['@media (max-width: 768px)']: {
      marginTop: "16px",
      fontSize: "12px",
      lineHeight: "16px",
      textAlign: "left",
    },
  },
  link: {
    display: "block",
    margin: "16px 0px",
    fontWeight: theme.typography.fontWeightSemibold,
    fontSize: "14px",
    lineHeight: "18px",
    textTransform: "uppercase",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
    "&$identificationRequired": {
      color: theme.palette.red
    },
    ['@media (max-width: 768px)']: {
      margin: "14px 0px",
      fontSize: "14px",
      lineHeight: "18px",
      textAlign: "center"
    },
  },
  sign: {
    fontSize: "10px",
    lineHeight: "14px",
    ['@media (max-width: 768px)']: {
      fontSize: "11px",
      lineHeight: "14px",
    },
  },
  identificationRequired: {

  },
  identificationPending: {

  },
  identified: {

  },
  loader: {
    margin: "4px 0px 23px"
  }
}));

const IdentificationInfoBlock = (props) => {
  const classes = blockStyles();
  const { className } = props ;

  const [currentUser, setCurrentUser] = useState(props.currentUser);
  const [infoClosed, setInfoClosed] = useState(WebIdIdentifiedInfoClosedStorage.get());

  useEffect(() => {
    setCurrentUser(props.currentUser);
  }, [props.currentUser]);

  const title = () => {
    return I18n.t(`web.identification.${UserModel.identificationStatus(currentUser)}.info.title`, {
      last_name: currentUser.personalInfo.lastName
    })
  }

  const text = () => {
    const status = UserModel.identificationStatus(currentUser);
    if(status === "identified" || status === "identification_pending") {
      return "";
    }
    return I18n.t(`web.identification.${status}.info.text`);
  }

  const onClickClose = () => {
    setInfoClosed(true);
    WebIdIdentifiedInfoClosedStorage.store();
  }

  if(UserModel.identificationStatus(currentUser) === "identified" && infoClosed) {
    return <React.Fragment />
  }

  return (
    <React.Fragment>
      { UserModel.identificationStatus(currentUser) !== "unidentified" &&
        <div className = { clsx({
          [classes.root]: true,
          [className]: true,
          [classes.identificationRequired]: UserModel.identificationStatus(currentUser) === "identification_required",
          [classes.identificationPending]: UserModel.identificationStatus(currentUser) === "identification_pending",
          [classes.identified]: UserModel.identificationStatus(currentUser) === "identified",
        }) } >
          <div className = { classes.content } >
            {
              UserModel.identificationStatus(currentUser) === "identification_pending" &&
              <CircularProgress className = { classes.loader }
                                size = { 30 } />
            }
            <HtmlContent className = { classes.title } >
              { title() }
            </HtmlContent>
            {
              UserModel.identificationStatus(currentUser) !== "identified" &&
              UserModel.identificationStatus(currentUser) !== "identification_pending" &&
              <React.Fragment>
                <HtmlContent className = { classes.text } >
                  { text() }
                </HtmlContent>
                <Link to = { Routes.profileIdentify() }
                      className = { clsx({
                        [classes.link]: true,
                        [classes.identificationRequired]: UserModel.identificationStatus(currentUser) === "identification_required",
                        [classes.identificationPending]: UserModel.identificationStatus(currentUser) === "identification_pending",
                        [classes.identified]: UserModel.identificationStatus(currentUser) === "identified",
                      }) } >
                  { I18n.t("action.go_to_web_id") }
                </Link>
                <HtmlContent className = { classes.sign } >
                  { I18n.t("web.identification.info.sign") }
                </HtmlContent>
              </React.Fragment>
            }
          </div>
          {
            UserModel.identificationStatus(currentUser) === "identified" &&
            <IconButton className = { classes.closeButton }
                        onClick = { onClickClose } >
              <CloseIcon className = { classes.closeIcon } />
            </IconButton>
          }
        </div>
      }
    </React.Fragment>
  );
}

export default compose(withCurrentUser)(IdentificationInfoBlock);
